<template>
  <div data-testid="shop-cart-item" class="flex w-full items-center gap-6 rounded-lg">
    <!-- Product info section -->
    <div class="flex min-w-0 flex-1 items-center gap-3">
      <!-- Product image -->
      <div class="flex h-[51px] w-[51px] shrink-0 items-center justify-center overflow-hidden rounded-sm bg-surface-elevation-2">
        <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.productName" class="h-full w-full object-cover" />
        <MdiSvg v-else :path="mdiPackageVariant" :size="32" class="text-subtle" />
      </div>

      <!-- Product details -->
      <div class="flex min-w-0 flex-1 flex-col gap-1">
        <div class="flex w-full flex-col">
          <h3 data-testid="shop-cart-item-name" class="truncate txt-body-l-bold text-main">{{ item.productName }}</h3>
          <p v-if="item.variantName" class="truncate txt-body-s text-subtle">
            {{ item.variantName }}
          </p>
        </div>
        <!-- Price info -->
        <div class="flex items-center gap-2">
          <p class="txt-body-s whitespace-nowrap text-subtlest">
            {{ t('FestivalShop.cart.pricePerItem', { price: formatPrice(item.price, locale as LocaleEnum) }) }}
          </p>
          <ChipBadge
            v-if="item.discountPercentage"
            data-testid="shop-cart-discount"
            color="error"
            rounded
            size="s"
            type="solid"
            class="txt-label-s-semibold!"
          >
            -{{ item.discountPercentage }}%
          </ChipBadge>
          <p v-if="item.originalPrice && item.originalPrice > item.price" class="txt-body-s whitespace-nowrap text-disabled line-through">
            {{ formatPrice(item.originalPrice, locale as LocaleEnum) }}
          </p>
        </div>
      </div>
    </div>

    <QuantitySelector
      :count="item.count"
      :max="item.maximum"
      :remove-when-zero="true"
      :max-reached-tooltip-z-index="60"
      @update:quantity="handleQuantityChange"
    />

    <!-- Line total -->
    <p data-testid="shop-cart-line-total" class="shrink-0 txt-body-s-bold whitespace-nowrap text-main">
      {{ formatPrice(lineTotal, locale as LocaleEnum) }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { mdiPackageVariant } from '@mdi/js'
import { MdiSvg, ChipBadge } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { LocaleEnum } from '@/gql/myomr'

import { formatPrice } from '../helpers'
import QuantitySelector from '../QuantitySelector.vue'

const { t, locale } = useI18n()

export interface CartItemData {
  productId: string
  productName: string
  variantId: string
  variantName?: string
  count: number
  maximum?: number
  price: number
  originalPrice?: number
  discountPercentage?: number
  deliveryFee?: number
  comment?: string
  file?: File[]
  imageUrl?: string
}

export interface Props {
  item: CartItemData
}
const props = defineProps<Props>()

const emit = defineEmits<{
  'update:quantity': [productId: string, variantId: string, count: number]
  remove: [productId: string, variantId: string]
}>()

const lineTotal = computed(() => {
  return props.item.price * props.item.count + (props.item.deliveryFee || 0)
})

const handleQuantityChange = (count: number) => {
  if (count === 0) {
    emit('remove', props.item.productId, props.item.variantId)
  } else {
    emit('update:quantity', props.item.productId, props.item.variantId, count)
  }
}
</script>
